import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Tag, X, CreditCard, Lock, AlertCircle, Loader2, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, clearCart } = useCart();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [shippingSettings, setShippingSettings] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: 'OH',
    zip_code: '',
    shipping_method: 'shipping'
  });

  // Check for cancelled checkout
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      toast.error('Payment was cancelled. Please try again.');
    }
  }, [searchParams]);

  useEffect(() => {
    if (!cart.items || cart.items.length === 0) {
      navigate('/cart');
      return;
    }
    fetchShippingSettings();
  }, [cart, navigate]);

  const fetchShippingSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/shipping`);
      setShippingSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch shipping settings', error);
    }
  };

  const getSubtotal = () => {
    return cart.items?.reduce((total, item) => {
      const price = item.product?.base_price || 0;
      return total + (price * item.quantity);
    }, 0) || 0;
  };

  const subtotal = getSubtotal();
  
  const calculateShipping = (orderTotal) => {
    if (formData.shipping_method === 'pickup') return 0;
    if (orderTotal >= 75) return 0;
    if (orderTotal >= 30) return 7.99;
    if (orderTotal >= 10) return 4.99;
    return 2.99;
  };
  
  const calculateDiscount = () => {
    if (!appliedPromo) return 0;
    if (appliedPromo.discount_type === 'percentage') {
      return subtotal * (appliedPromo.discount_value / 100);
    }
    return appliedPromo.discount_value;
  };
  
  const discount = calculateDiscount();
  const subtotalAfterDiscount = subtotal - discount;
  const shippingCost = calculateShipping(subtotalAfterDiscount);
  const taxRate = shippingSettings?.tax_rate || 0.0725;
  const tax = subtotalAfterDiscount * taxRate;
  const total = subtotalAfterDiscount + shippingCost + tax;

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    
    setPromoLoading(true);
    setPromoError('');
    
    try {
      const response = await axios.post(`${API}/promo/validate`, { code: promoCode });
      setAppliedPromo(response.data);
      toast.success(`Promo code "${promoCode.toUpperCase()}" applied!`);
      setPromoCode('');
    } catch (error) {
      setPromoError(error.response?.data?.detail || 'Invalid promo code');
      toast.error('Invalid promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    toast.info('Promo code removed');
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const validateForm = () => {
    if (!formData.first_name || !formData.last_name) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.email) {
      toast.error('Please enter your email');
      return false;
    }
    if (!formData.phone) {
      toast.error('Please enter your phone number');
      return false;
    }
    if (formData.shipping_method === 'shipping') {
      if (!formData.address || !formData.city || !formData.state || !formData.zip_code) {
        toast.error('Please enter your complete shipping address');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      // Create order data - clean up variant_info to only include string values
      const orderItems = cart.items.map(item => {
        const variant = item.product.variants?.[item.variant_index];
        // Only include string fields from variant, exclude stock and non-string values
        let cleanVariantInfo = null;
        if (variant) {
          cleanVariantInfo = {};
          if (variant.size) cleanVariantInfo.size = String(variant.size);
          if (variant.color) cleanVariantInfo.color = String(variant.color);
          if (variant.sku) cleanVariantInfo.sku = String(variant.sku);
          // Only include if we have at least one field
          if (Object.keys(cleanVariantInfo).length === 0) cleanVariantInfo = null;
        }
        
        // Get the product image (first image from the images array)
        const productImage = item.product.images?.[0] || item.product.image || null;
        
        return {
          product_id: item.product.id,
          product_name: item.product.name,
          product_image: productImage,
          variant_info: cleanVariantInfo,
          quantity: item.quantity,
          price: item.product.base_price,
          subtotal: item.product.base_price * item.quantity
        };
      });

      const orderData = {
        user_id: user?.id || null,
        items: orderItems,
        shipping_address: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code
        },
        shipping_method: formData.shipping_method,
        shipping_cost: shippingCost,
        tax: tax,
        subtotal: subtotal,
        discount: discount,
        promo_code: appliedPromo?.code || null,
        total: total
      };

      // Mark US Insurance code as used if applicable
      if (appliedPromo?.is_usinsure) {
        try {
          await axios.post(`${API}/promo/mark-used`, { code: appliedPromo.code });
        } catch (err) {
          console.error('Failed to mark promo as used', err);
        }
      }

      // Create checkout session - this will return Clover redirect URL
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/checkout/create-session`,
        { 
          order: orderData,
          origin_url: window.location.origin
        },
        { headers }
      );

      if (response.data.checkout_url) {
        // Store order info in sessionStorage for recovery if user returns
        sessionStorage.setItem('pending_order_id', response.data.order_id);
        sessionStorage.setItem('pending_session_id', response.data.session_id);
        
        // DON'T clear cart here - only clear after successful payment in OrderSuccess
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout failed', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create checkout. Please try again.';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1
          className="text-4xl font-bold mb-8"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="checkout-title"
        >
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form */}
          <form onSubmit={handleSubmit} data-testid="checkout-form">
            {/* Shipping Method */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4">Shipping Method</h2>
              <div className="space-y-3">
                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:border-[#D4AF37] transition-colors">
                  <input
                    type="radio"
                    name="shipping_method"
                    value="shipping"
                    checked={formData.shipping_method === 'shipping'}
                    onChange={handleChange}
                    className="mr-3"
                    data-testid="shipping-method-delivery"
                  />
                  <div className="flex-1">
                    <div className="font-medium">Delivery</div>
                    <div className="text-sm text-gray-600">
                      {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                    </div>
                  </div>
                </label>
                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:border-[#D4AF37] transition-colors">
                  <input
                    type="radio"
                    name="shipping_method"
                    value="pickup"
                    checked={formData.shipping_method === 'pickup'}
                    onChange={handleChange}
                    className="mr-3"
                    data-testid="shipping-method-pickup"
                  />
                  <div className="flex-1">
                    <div className="font-medium">Local Pickup</div>
                    <div className="text-sm text-gray-600">FREE - Pick up at store</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    data-testid="input-last-name"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  data-testid="input-email"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  data-testid="input-phone"
                />
              </div>
            </div>

            {/* Shipping Address */}
            {formData.shipping_method === 'shipping' && (
              <div className="mb-8">
                <h2 className="text-xl font-bold mb-4">Shipping Address</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required={formData.shipping_method === 'shipping'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                      data-testid="input-address"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required={formData.shipping_method === 'shipping'}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">State *</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required={formData.shipping_method === 'shipping'}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                        data-testid="input-state"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">ZIP Code *</label>
                      <input
                        type="text"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleChange}
                        required={formData.shipping_method === 'shipping'}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                        data-testid="input-zip"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Notice */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment
              </h2>
              
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-blue-900">Secure Payment via Stripe</p>
                    <p className="text-sm text-blue-700 mt-1">
                      You will be redirected to Stripe's secure payment page to complete your purchase.
                    </p>
                    
                    {/* Card Payment Methods */}
                    <div className="mt-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Accepted Cards</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Visa */}
                        <div className="bg-white rounded-md px-3 py-2 border border-gray-200 shadow-sm">
                          <svg className="h-5 w-auto" viewBox="0 0 780 500" fill="none">
                            <rect width="780" height="500" rx="40" fill="#1A1F71"/>
                            <path d="M293.2 348.7l33.4-195h53.4l-33.4 195h-53.4zm213.4-190.4c-10.5-4-27-8.3-47.5-8.3-52.4 0-89.4 26.3-89.7 63.9-.3 27.8 26.4 43.3 46.5 52.6 20.7 9.5 27.6 15.5 27.5 24-.1 13-16.5 18.9-31.7 18.9-21.2 0-32.5-2.9-49.9-10.2l-6.8-3.1-7.4 43.1c12.4 5.4 35.3 10.1 59.1 10.3 55.8 0 92-26 92.5-66.2.2-22.1-14-38.9-44.7-52.8-18.6-9-30-15-29.9-24.2.1-8.1 9.6-16.8 30.4-16.8 17.4-.3 30 3.5 39.8 7.4l4.8 2.2 7.2-41.9m136.6-4.9h-41c-12.7 0-22.2 3.5-27.8 16.1l-78.8 177.8h55.7s9.1-23.9 11.2-29.1h68.1c1.6 6.8 6.5 29.1 6.5 29.1h49.3l-43.2-193.9zm-65.6 125.1c4.4-11.2 21.2-54.3 21.2-54.3-.3.5 4.4-11.3 7.1-18.6l3.6 16.8s10.2 46.3 12.3 56.1h-44.2zM285 153.3l-51.9 133-5.5-26.7c-9.6-30.7-39.6-64-73.1-80.6l47.5 169h56.2l83.6-195h-56.8z" fill="white"/>
                            <path d="M146.9 153.3H59.4l-.6 3.6c66.7 16.1 110.9 55 129.2 101.7l-18.6-89.2c-3.2-12.2-12.5-15.7-22.5-16.1z" fill="#F9A51A"/>
                          </svg>
                        </div>
                        {/* Mastercard */}
                        <div className="bg-white rounded-md px-3 py-2 border border-gray-200 shadow-sm">
                          <svg className="h-5 w-auto" viewBox="0 0 780 500" fill="none">
                            <rect width="780" height="500" rx="40" fill="white"/>
                            <circle cx="299" cy="250" r="158" fill="#EB001B"/>
                            <circle cx="481" cy="250" r="158" fill="#F79E1B"/>
                            <path d="M390 117c47.4 35.3 78 91.8 78 155s-30.6 119.7-78 155c-47.4-35.3-78-91.8-78-155s30.6-119.7 78-155z" fill="#FF5F00"/>
                          </svg>
                        </div>
                        {/* American Express */}
                        <div className="bg-white rounded-md px-3 py-2 border border-gray-200 shadow-sm">
                          <svg className="h-5 w-auto" viewBox="0 0 780 500" fill="none">
                            <rect width="780" height="500" rx="40" fill="#016FD0"/>
                            <path d="M78 250h73l16-38 17 38h297v-29l26 29h150v-150H508l-24 28-23-28H233l-34 77-35-77H78v150zm106-30H141v-90h43l19 43 20-43h43v90h-33v-56l-24 56h-23l-23-56v56h-8zm134-90h95l22 26 23-26h39l-40 45 40 45h-40l-23-26-22 26h-94v-90zm33 60h56l-28-30-28 30zm-33 30h33v-30h-33v30zm231-90v90h-41l-35-55v55h-52l-11-25h-57l-11 25h-37l50-90h46l47 84v-84h51l32 49v-49h18zm-168 64l-19-44-19 44h38zm56 26v-30h33v30h-33z" fill="white"/>
                          </svg>
                        </div>
                        {/* Discover */}
                        <div className="bg-white rounded-md px-3 py-2 border border-gray-200 shadow-sm">
                          <svg className="h-5 w-auto" viewBox="0 0 780 500" fill="none">
                            <rect width="780" height="500" rx="40" fill="#F5F5F5"/>
                            <path d="M177 221c-9.8-7.7-24.2-11.5-43-11.5H96v123h38c18.8 0 33.2-3.8 43-11.5 9.8-7.6 14.7-18.6 14.7-33v-34c0-14.4-4.9-25.4-14.7-33z" fill="#231F20"/>
                            <circle cx="470" cy="250" r="100" fill="#F48120"/>
                            <path d="M227 209.5h30v123h-30v-123zm47 0h35l27 80 27-80h35v123h-28v-92l-30 92h-8l-30-92v92h-28v-123zm273 0h-98v123h98v-24h-68v-27h66v-24h-66v-24h68v-24z" fill="#231F20"/>
                            <path d="M596 209.5h38l-38 62 42 61h-40l-38-61 38-62z" fill="#231F20"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                    
                    {/* Express Checkout Methods */}
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Express Checkout</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Apple Pay */}
                        <div className="bg-black rounded-md px-3 py-2 border border-gray-800 shadow-sm flex items-center gap-1">
                          <svg className="h-5 w-auto" viewBox="0 0 165 105" fill="none">
                            <rect width="165" height="105" rx="10" fill="black"/>
                            <path d="M47.2 35.4c2.3-2.9 3.8-6.9 3.4-10.9-3.3.1-7.2 2.2-9.6 5-2.1 2.5-3.9 6.5-3.4 10.3 3.6.3 7.3-1.8 9.6-4.4zm3.4 5.6c-5.3-.3-9.8 3-12.4 3-.3 0-6.2-.3-10.6-4.7-4.3 2.5-6.8 6.7-6.8 12.2 0 9.6 8.3 22.4 14.7 22.4 1.9 0 5.4-1.4 9.1-1.4 3.6 0 6.3 1.4 9 1.4 8.1 0 14.9-17.2 14.9-17.2-9.4-3.5-11-17.2-2.9-22.4-4-4.8-10-6.2-15-5.7zm55.6-8.6V74h7.6V56.8h10.5c9.6 0 16.3-6.6 16.3-16.2s-6.6-16.2-16.1-16.2h-18.3zm7.6 6.6h8.7c6.6 0 10.4 3.5 10.4 9.7 0 6.1-3.8 9.7-10.4 9.7h-8.7V39zm45.9 35c4.8 0 9.2-2.4 11.2-6.3h.2V74h7.1V51.6c0-7.1-5.7-11.7-14.5-11.7-8.1 0-14.1 4.7-14.3 11.1h6.9c.6-3 3.4-5 7.2-5 4.7 0 7.3 2.2 7.3 6.2v2.7l-9.6.6c-8.9.5-13.7 4.2-13.7 10.5 0 6.5 5 10.9 12.2 10.9zm2-5.8c-4.1 0-6.7-2-6.7-5 0-3.1 2.5-4.9 7.3-5.2l8.5-.5v2.8c0 4.6-3.9 7.9-9.1 7.9z" fill="white"/>
                          </svg>
                        </div>
                        {/* Google Pay */}
                        <div className="bg-white rounded-md px-3 py-2 border border-gray-200 shadow-sm flex items-center">
                          <svg className="h-5 w-auto" viewBox="0 0 165 105" fill="none">
                            <rect width="165" height="105" rx="10" fill="white" stroke="#DADCE0"/>
                            <path d="M77.4 56.4v14.8h-4.7V35.4h12.4c3 0 5.6 1 7.6 3.1 2.1 2 3.1 4.5 3.1 7.4 0 3-1 5.4-3.1 7.4-2 2-4.5 3-7.6 3h-7.7zm0-16.6v12.2h7.9c1.8 0 3.3-.6 4.5-1.9 1.2-1.2 1.8-2.7 1.8-4.3 0-1.6-.6-3-1.8-4.2-1.2-1.2-2.7-1.9-4.5-1.9h-7.9zm30 6.2c3.5 0 6.2.9 8.2 2.8 2 1.9 3 4.4 3 7.6v15.2h-4.5v-3.4h-.2c-1.9 2.8-4.5 4.2-7.7 4.2-2.8 0-5.1-.8-6.9-2.5-1.8-1.6-2.7-3.7-2.7-6.1 0-2.6.9-4.6 2.8-6.2 1.9-1.5 4.4-2.3 7.5-2.3 2.7 0 4.9.5 6.6 1.5v-1c0-1.5-.6-2.8-1.7-3.8-1.2-1-2.5-1.5-4.1-1.5-2.4 0-4.2.9-5.6 2.8l-4.1-2.6c2.1-3 5.2-4.6 9.4-4.6zm-5.8 18.1c0 1.1.5 2.1 1.4 2.8.9.8 2 1.1 3.2 1.1 1.8 0 3.4-.7 4.7-2 1.4-1.3 2-2.8 2-4.5-1.4-1.2-3.3-1.8-5.8-1.8-1.8 0-3.3.4-4.4 1.3-1.1.9-1.1 1.9-1.1 3.1zm36.6-17.3l-15.3 35.2h-4.8l5.7-12.2-10-23h5.1l7.1 17.2h.1l6.9-17.2h5.2z" fill="#5F6368"/>
                            <path d="M56.3 52.8c0-1.4-.1-2.7-.4-4H40.7v7.6h8.8c-.4 2-1.5 3.7-3.2 4.9v4h5.2c3-2.8 4.8-6.9 4.8-12.5z" fill="#4285F4"/>
                            <path d="M40.7 69c4.3 0 8-1.4 10.6-3.9l-5.2-4c-1.4 1-3.3 1.5-5.4 1.5-4.2 0-7.7-2.8-9-6.6h-5.4v4.1c2.7 5.4 8.2 8.9 14.4 8.9z" fill="#34A853"/>
                            <path d="M31.7 56c-.3-1-.5-2-.5-3.1 0-1.1.2-2.1.5-3.1v-4.1h-5.4c-1.1 2.2-1.7 4.7-1.7 7.2s.6 5 1.7 7.2l5.4-4.1z" fill="#FBBC04"/>
                            <path d="M40.7 43.2c2.4 0 4.5.8 6.1 2.4l4.6-4.6c-2.8-2.6-6.4-4.2-10.7-4.2-6.2 0-11.7 3.5-14.4 8.9l5.4 4.1c1.3-3.8 4.8-6.6 9-6.6z" fill="#EA4335"/>
                          </svg>
                        </div>
                        {/* Stripe Link */}
                        <div className="bg-[#00D66F] rounded-md px-3 py-2 border border-[#00B85E] shadow-sm flex items-center gap-1">
                          <svg className="h-5 w-auto" viewBox="0 0 165 105" fill="none">
                            <rect width="165" height="105" rx="10" fill="#00D66F"/>
                            <path d="M40 50.5h7.5v20h-7.5v-20zm7.5-10c0 2.5-2 4.5-4.5 4.5s-4.5-2-4.5-4.5 2-4.5 4.5-4.5 4.5 2 4.5 4.5zm10 10h7.2v2.8h.1c1-1.9 3.5-3.8 7.1-3.8 7.6 0 9 5 9 11.5v12.5h-7.5V62c0-3.1-.1-7.1-4.3-7.1-4.3 0-5 3.4-5 6.9v8.7H57v-20zm30 0h7.2v2.8h.1c1-1.9 3.5-3.8 7.1-3.8 7.6 0 9 5 9 11.5v12.5h-7.5V62c0-3.1-.1-7.1-4.3-7.1-4.3 0-5 3.4-5 6.9v8.7H87v-20zm55.5 0h-7.5v20h7.5v-10.2l7.5 10.2h9l-9.5-11 9-9h-8.5l-7.5 8.2v-18.2h-7.5v10z" fill="white"/>
                            <circle cx="135" cy="56" r="5" fill="white"/>
                          </svg>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Express options appear automatically on supported devices
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-white py-4 rounded-full hover:bg-[#B5952F] transition-colors font-medium text-lg disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="complete-order-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Order...
                </>
              ) : (
                <>
                  <ExternalLink className="w-5 h-5" />
                  Proceed to Payment - ${total.toFixed(2)}
                </>
              )}
            </button>
            
            <p className="text-center text-sm text-gray-500 mt-4">
              You'll be securely redirected to complete payment
            </p>
          </form>

          {/* Order Summary */}
          <div>
            <div className="bg-[#F9F9F9] p-6 rounded-lg sticky top-24" data-testid="checkout-summary">
              <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                {cart.items?.map((item, idx) => {
                  const product = item.product;
                  if (!product) return null;
                  
                  return (
                    <div key={`${item.product_id}-${item.variant_index}`} className="flex gap-3" data-testid={`summary-item-${idx}`}>
                      <img
                        src={product.images[0] || 'https://via.placeholder.com/80'}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium">{product.name}</h4>
                        <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                        <p className="text-sm font-semibold">${(product.base_price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Promo Code Section */}
              <div className="border-t pt-4 mb-4">
                <label className="block text-sm font-medium mb-2">Promo Code</label>
                {appliedPromo ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">{appliedPromo.code}</span>
                      <span className="text-xs text-green-600">
                        ({appliedPromo.discount_type === 'percentage' ? `${appliedPromo.discount_value}% off` : `$${appliedPromo.discount_value} off`})
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemovePromo}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                      data-testid="promo-code-input"
                    />
                    <button
                      type="button"
                      onClick={handleApplyPromo}
                      disabled={promoLoading || !promoCode.trim()}
                      className="px-4 py-2 bg-[#1A1A1A] text-white text-sm font-medium rounded-md hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      data-testid="apply-promo-button"
                    >
                      {promoLoading ? '...' : 'Apply'}
                    </button>
                  </div>
                )}
                {promoError && <p className="text-xs text-red-500 mt-1">{promoError}</p>}
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span data-testid="summary-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount ({appliedPromo?.code})</span>
                    <span data-testid="summary-discount">-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span>Shipping</span>
                  <span data-testid="summary-shipping">
                    {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Tax</span>
                  <span data-testid="summary-tax">${tax.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span data-testid="summary-total">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
